// Web Audio bus. Single owner of the AudioContext.
// Architecture: docs/architecture.md (tech stack — no <audio> elements).
// Tuning: BGM_VOLUME / SFX_VOLUME / fade times live in config/gameplay.ts.

import {
  BGM_FADE_IN_S,
  BGM_FADE_OUT_S,
  BGM_VOLUME,
  SFX_VOLUME,
} from "../config/gameplay";

export class AudioBus {
  readonly ctx: AudioContext;
  // Master gain sits between (bgm+sfx) and the destination so a single node
  // mutes everything without disturbing the BGM fade ramps on bgmGain.
  private masterGain: GainNode;
  private bgmGain: GainNode;
  private sfxGain: GainNode;
  private bgmSource: AudioBufferSourceNode | null = null;
  private muted = false;
  // Cached one-shot SFX that doesn't belong to a theme — loaded once at init,
  // played on each successful capture in the Capture phase.
  private shutterBuffer: AudioBuffer | null = null;

  constructor() {
    const Ctor: typeof AudioContext =
      (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 1;
    this.masterGain.connect(this.ctx.destination);
    this.bgmGain = this.ctx.createGain();
    this.bgmGain.gain.value = 0;
    this.bgmGain.connect(this.masterGain);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = SFX_VOLUME;
    this.sfxGain.connect(this.masterGain);
  }

  async resume(): Promise<void> {
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  /** Session-wide mute. Persists while the singleton lives, so toggling it in
   *  one phase (e.g. Play's sound button) carries across screens. */
  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(muted ? 0 : 1, now);
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  playBGM(buffer: AudioBuffer): void {
    this.stopBGM(true);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(this.bgmGain);
    src.start();
    this.bgmSource = src;
    const now = this.ctx.currentTime;
    this.bgmGain.gain.cancelScheduledValues(now);
    this.bgmGain.gain.setValueAtTime(0, now);
    this.bgmGain.gain.linearRampToValueAtTime(BGM_VOLUME, now + BGM_FADE_IN_S);
  }

  stopBGM(immediate = false): void {
    if (!this.bgmSource) return;
    const src = this.bgmSource;
    this.bgmSource = null;
    if (immediate) {
      try {
        src.stop();
      } catch {
        // already stopped
      }
      return;
    }
    const now = this.ctx.currentTime;
    this.bgmGain.gain.cancelScheduledValues(now);
    this.bgmGain.gain.setValueAtTime(this.bgmGain.gain.value, now);
    this.bgmGain.gain.linearRampToValueAtTime(0, now + BGM_FADE_OUT_S);
    setTimeout(() => {
      try {
        src.stop();
      } catch {
        // already stopped
      }
    }, BGM_FADE_OUT_S * 1000);
  }

  playSFX(buffer: AudioBuffer): void {
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.sfxGain);
    src.start();
  }

  async loadShutter(url: string): Promise<void> {
    if (this.shutterBuffer) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`shutter fetch failed: ${res.status}`);
      const buf = await res.arrayBuffer();
      this.shutterBuffer = await this.ctx.decodeAudioData(buf);
    } catch (err) {
      console.error("[AudioBus] shutter sfx load failed", err);
    }
  }

  playShutter(): void {
    if (!this.shutterBuffer) return;
    this.playSFX(this.shutterBuffer);
  }
}
